// The area tool (issue 832, BOARD-016): the mode in which an area template is
// drafted — shape, size, direction, label from the composer; the point of
// origin from a click on the board (a corner between squares) — and shown as
// a preview until it is laid. Held outside React, like the pointing tool.
import type { AreaShape, Cell } from '@dragons-table/vtt-shared';
import { wallTool } from './wallTool';
import { focusTool } from './focusTool';
import { measureTool } from './measureTool';
import { fogTool } from './fogTool';
import { zoneTool } from './zoneTool';
import { drawTool } from './drawTool';
import { targetTool } from './targetTool';
import { whatsThisTool } from './whatsThisTool';

export interface AreaDraft {
  on: boolean;
  shape: AreaShape;
  /** In squares. */
  size: number;
  size2: number;
  dir: number;
  label: string;
  /** The corner it starts from; null until the board is clicked. */
  origin: Cell | null;
  /**
   * A spell cast from the action bar (issue 1130): its shape and size are fixed, the composer stays shut, and the first
   * tap on the board hands the corner to the bar (`place`) instead of drafting it.
   */
  cast: boolean;
}

type Listener = (d: AreaDraft) => void;
/** Sizes in squares (issue 1303): the composer shows them in the system's unit. */
const initial = (): AreaDraft => ({ on: false, shape: 'burst', size: 4, size2: 4, dir: 0, label: '', origin: null, cast: false });
let draft: AreaDraft = initial();
let onCast: ((origin: Cell, d: AreaDraft) => void) | null = null;
const listeners = new Set<Listener>();
const set = (next: AreaDraft) => { draft = next; for (const l of listeners) l(draft); };

export const areaTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  start(): void { whatsThisTool.stop(); focusTool.stop(); measureTool.stop(); fogTool.stop(); drawTool.stop(); wallTool.stop(); zoneTool.stop(); targetTool.stop(); onCast = null; set({ ...initial(), on: true }); },
  /** Cast a spell from the action bar (issue 1130): the preview follows the pointer; the next tap is `place`. */
  cast(spell: Pick<AreaDraft, 'shape' | 'size' | 'size2' | 'label'>, place: (origin: Cell, d: AreaDraft) => void): void {
    areaTool.start();
    onCast = place;
    set({ ...draft, ...spell, cast: true });
  },
  stop(): void { onCast = null; if (draft.on) set(initial()); },
  /** A tap on the board while laying an area: a cast's corner goes to the bar and the mode ends; a draft's is moved. */
  place(origin: Cell): void {
    if (!draft.on) return;
    if (!draft.cast) { areaTool.change({ origin }); return; }
    const f = onCast; const d = { ...draft, origin };
    areaTool.stop();
    f?.(origin, d);
  },
  toggle(): void { if (draft.on) areaTool.stop(); else areaTool.start(); },
  change(patch: Partial<Omit<AreaDraft, 'on'>>): void { if (draft.on) set({ ...draft, ...patch }); },
};
